import { TFunction } from 'i18next';

import { FEATURES } from 'appConstants';
import { cdnURL } from 'utils/helpers';
import i18n from 'utils/i18n';

import { TCampaignCreatePage } from './i18n/TModules';

export interface IOutcomeSetting {
  headline?: string;
  button_text?: string;
  image?: { type: string; value: Record<string, any> };
}

export type OutcomeSettings = Record<string, IOutcomeSetting>;

const OUTCOME_SETTINGS = `${TCampaignCreatePage}.LandingPage.OutcomeSettings`;

const withCampaignName = (headline: string, campaignName?: string) =>
  campaignName ? headline.replace('{campaign_name}', campaignName) : headline;

/**
 * The copy and artwork a claw campaign shows in its win / no-win popup. Every path that needs them
 * resolves them here: picking the game type on create, and normalising an existing campaign for the
 * edit form.
 */
export const getClawOutcomeSettingsDefaults = (
  t: TFunction,
  campaignName?: string
): OutcomeSettings => ({
  vouchers: {
    headline: t(`${OUTCOME_SETTINGS}.ClawVouchersHeadline`),
    button_text: t(`${OUTCOME_SETTINGS}.PlayAgainButtonText`),
    image: {
      type: 'image',
      value: { filename: 'prize.png', image_url: cdnURL('outcome-settings/voucher.svg') }
    }
  },
  points: {
    headline: t(`${OUTCOME_SETTINGS}.ClawPointsHeadline`),
    button_text: t(`${OUTCOME_SETTINGS}.PlayAgainButtonText`),
    image: {
      type: 'image',
      value: { filename: 'prize.png', image_url: cdnURL('outcome-settings/points.svg') }
    }
  },
  badges: {
    headline: t(`${OUTCOME_SETTINGS}.ClawBadgesHeadline`),
    button_text: t(`${OUTCOME_SETTINGS}.PlayAgainButtonText`),
    image: {
      type: 'image',
      value: { filename: 'prize.png', image_url: cdnURL('outcome-settings/badge.svg') }
    }
  },
  no_outcome: {
    headline: t(`${OUTCOME_SETTINGS}.NoOutcomeHeadline`),
    button_text: t(`${OUTCOME_SETTINGS}.PlayAgainButtonText`),
    image: {
      type: 'image',
      value: {
        filename: 'nooutcome.gif',
        image_url: 'https://cdn.perxtech.net/content/dashboard/nooutcome.gif'
      }
    }
  },
  // Game Try is a claw outcome here and suppressed on master, so this tab exists on uat only.
  game_tries: {
    headline: withCampaignName(t(`${OUTCOME_SETTINGS}.GameTriesHeadline`), campaignName),
    button_text: t(`${OUTCOME_SETTINGS}.GameTriesButtonText`),
    image: {
      type: 'image',
      value: {
        filename: 'game_try_icon.png',
        image_url: cdnURL('outcome-settings/game_try_icon.png')
      }
    }
  }
});

/**
 * Merges per field, not per object: a campaign that stored only a headline keeps its headline and
 * gains the default image, and a field the maker deliberately cleared to '' stays cleared. An image
 * is taken whole — filling `image_url` into an image the maker uploaded as a base64 `file` would put
 * two sources on the wire.
 */
export const withOutcomeSettingsDefaults = (
  stored: OutcomeSettings | null | undefined,
  defaults: OutcomeSettings
): OutcomeSettings =>
  Object.entries(defaults).reduce(
    (settings, [outcomeType, outcomeDefaults]) => {
      const current = settings[outcomeType];

      if (!current) {
        return { ...settings, [outcomeType]: outcomeDefaults };
      }

      const authored = Object.fromEntries(
        Object.entries(current).filter(([, value]) => value !== undefined && value !== null)
      );

      return { ...settings, [outcomeType]: { ...outcomeDefaults, ...authored } };
    },
    { ...(stored || {}) } as OutcomeSettings
  );

/**
 * The one place that decides whether a campaign being loaded into a form gets the claw defaults.
 * Every entry point into the campaign form — edit, duplicate — resolves it through here, so adding
 * another cannot reintroduce the blank-tab bug by forgetting to pass them. The campaign is passed
 * too because the Game Try headline carries its name.
 */
export const clawNormalizeOptions = (
  features: Record<string, any> | undefined,
  campaign?: { name?: string }
) =>
  features?.[FEATURES.OUTCOME_POPUP_MESSAGE_SETTINGS.key]
    ? { outcomeSettingsDefaults: getClawOutcomeSettingsDefaults(i18n.t, campaign?.name) }
    : {};
